import { Image } from 'components/Image'
import { useEffect, useState, useContext, useMemo } from 'react'
import { debounce as _debounce } from 'lodash-es'
import useMobileNavState from 'hooks/useMobileNavState'
import useIsSsr from 'hooks/useIsSsr'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { Box, Button, Typography, AchieveTheme } from '@achieve/ascend'
import Container from '@mui/material/Container'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { SignInLink } from 'components/SignInLink'
import Navigation from 'components/Navigation'
import HeaderMobileActions from './HeaderMobileActions'
import HeaderCta from './HeaderCta'
import { handleTrackAndReactEvent } from 'utils/analytics'
import useLayoutMenuCondensed from 'hooks/useLayoutMenuCondensed'
import { LayoutContext } from 'providers/LayoutProvider'
import { X as CloseIcon, Plus } from 'react-feather'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { PhoneDataContext } from 'providers/PhoneDataProvider'
import { MediaImageStandard } from 'components/Contentful'
import MenuIcon from 'components/Svgs/Menu.js'
import Close from 'components/Svgs/Close.js'
import { mainProductConfig } from 'constants/mainProductConfig'
import { FdrPage, FfnPage, AchievePage } from 'utils/environmentConfig'
import classNames from 'classnames'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { get as _get } from 'lodash-es'
import { Typography as OwnerTypography } from 'components/Contentful'
import { useGraduate } from 'providers/GraduateProvider'
import { WALL_OF_FAME_ROUTE } from 'constants/navigation'
import { STICKY_FOOTER_BLACKLIST } from 'components/Footer/FooterSticky/FooterSticky'
import { CurrentSvg } from 'components/CurrentSvg'
import { FDRHeader } from './FDRHeader/FDRHeader'
import { useAnyElementInViewport } from 'hooks/useAnyElementInViewport'
import { getSubdomain } from '@/utils/shared'

// Percentage of the window that needs to scroll for the mobile header to condense
const MOBILE_CONDENSED_THRESHOLD = 0.3

/**
 * Main site Header component
 * @param {{
 *  mainNavigation: object
 *  disabledRoutes: []
 * }} props
 */
export default function Header({
  disabledElements,
  disabledRoutes,
  mainNavigation,
  logoEvent,
  signInLinkUrl,
  signInLinkText,
  signInEvent,
  linkUrl,
  linkText,
  headerCta = true,
  linkEvent,
  showNav = true,
  showSignIn = true,
  customPhoneData = null,
  partnerLogo,
  logoLink = true,
  showProgressPage = false,
  siteEnv,
  footerSticky,
  showPhoneNumber = false,
  showDebtConsultantCta = false,
  stickyHeaderSettings = {},
}) {
  const [, setLayoutMenuCondensed] = useLayoutMenuCondensed()
  const { fireAndForget } = useTriggerEvents()
  const [mobileNavOpen, setMobileNavOpen] = useMobileNavState()
  const [condensed, setCondensed] = useState(false)
  const [mobileCondensed, setMobileCondensed] = useState(false)
  const isSsr = useIsSsr()
  const mqHookResults = useViewportSmallerThan(BREAKPOINTS.lg)
  const [buttonCall, setButtonCall] = useState(false)
  const [backLink, setBackLink] = useState('/')
  let phoneData = useContext(PhoneDataContext)

  const { state: { isMobileUA } = {} } = useContext(LayoutContext)
  const phoneIconUrl = '/multi-site-assets/icon-phone.svg'
  const phoneIconBlueUrl = '/multi-site-assets/phone-blue.svg'

  const [progressPage, setProgressPage] = useState(0)
  const { dynamicPathname, params } = useAppRouter()
  const tp_text = _get(footerSticky, 'card.subtitle')
  const tp_image = _get(footerSticky, 'card.image')

  const { graduate } = useGraduate()

  const stickyFooterDisabled =
    // 1. don't display if the active route is in the blacklist
    // 2. don't display if the active route is the Wall of Fame graduate tokenized route
    [...STICKY_FOOTER_BLACKLIST, ...(graduate ? [WALL_OF_FAME_ROUTE] : [])].includes(
      dynamicPathname
    )

  const showStickyFooter = !stickyFooterDisabled
  /**
   * The below logic is used specifically for home-equity-loans-mp. AHL Direct Mail campaign
   * requires phone number to be based on utm_source and utm_content. Phone number data is
   * fetched from pageConfig object in Home Equity Loan MP contentful page entry.
   * Implemented by PXP team.
   * Contact: Shreyas Dorle, Brandon Chapman
   */
  if (customPhoneData) {
    const { utm_source, utm_content, ahl_dm_creatives } = customPhoneData ?? {}
    const {
      informed_delivery,
      digital,
      default_values,
      utm_source: ahl_dm_utm_source,
    } = ahl_dm_creatives ?? {}
    if (utm_source === ahl_dm_utm_source) {
      if (utm_content?.toUpperCase() === informed_delivery?.utm_content) {
        phoneData.phoneNumber = informed_delivery?.phone
      } else if (utm_content?.toUpperCase() === digital?.utm_content) {
        phoneData.phoneNumber = digital?.phone
      } else {
        phoneData.phoneNumber = default_values?.phone
      }
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol
      const host = window.location.host
      if (host) {
        if (getSubdomain(host, 'legal')) {
          const url = `${protocol}//${host}`.replaceAll('legal.', '').replaceAll('legal-', '')
          setBackLink(url)
        } else {
          setBackLink('/')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Default to mobile first before initial render if the user agent matches a mobile device
  const isMobile = isSsr ? isMobileUA : mqHookResults

  const debounceOptions = {
    leading: true,
    trailing: false,
  }

  // showStickyHeaderOnLeave must be an array of html IDs
  const onViewport = useAnyElementInViewport(
    stickyHeaderSettings?.showStickyHeaderOnLeave,
    siteEnv === FdrPage && !isMobile ? 120 : 0
  )

  const setMobileCondensedTrue = _debounce(() => setMobileCondensed(true), 100, debounceOptions)
  const setMobileCondensedFalse = _debounce(() => setMobileCondensed(false), 100, debounceOptions)
  const setCondensedTrue = _debounce(() => setCondensed(true), 100, debounceOptions)
  const setCondensedFalse = _debounce(() => setCondensed(false), 100, debounceOptions)

  const isDebtConsolidation = params.slug === 'debt-consolidation'

  /**
   * Set the initial condensed state based on window scroll position
   */
  useEffect(() => {
    if (isSsr) {
      // no window to measure during SSR
      return
    }

    onScroll()
    // This effect is only intended to run on the first browser (not SSR) render
  }, [isSsr]) /* eslint-disable-line react-hooks/exhaustive-deps */

  useEffect(() => {
    const handleScroll = _debounce(() => {
      onScroll()
    }, 30)
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  })

  function onScroll() {
    const currentScroll = window.scrollY
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
    const scrollPercentage = (currentScroll / scrollHeight) * 100
    setProgressPage(scrollPercentage)

    if (isMobile) {
      return handleMobileScroll()
    }
    return handleDesktopScroll()
  }

  /**
   * Enable the condensed mobile header when the page is scrolled passed the threshold
   */
  function handleMobileScroll() {
    const currentScroll = window.scrollY
    const nextMobileCondensed = currentScroll > window.innerHeight * MOBILE_CONDENSED_THRESHOLD

    if (nextMobileCondensed === mobileCondensed && !onViewport) {
      return
    }

    if (nextMobileCondensed && !onViewport) {
      return setMobileCondensedTrue()
    }

    return setMobileCondensedFalse()
  }

  /**
   * Enable the condensed desktop header when the page is scrolled any distance from the top
   */
  function handleDesktopScroll() {
    const currentScroll = window.scrollY
    const nextCondensed = Boolean(currentScroll)

    if (nextCondensed === condensed && !onViewport) {
      return
    }

    if (nextCondensed && !onViewport) {
      return setCondensedTrue()
    }

    return setCondensedFalse()
  }

  /*
    Prevent body scroll when the mobile navigation menu is open
  */
  useEffect(() => {
    const body = document.querySelector('body')

    if (!body) {
      return
    }

    if (isMobile && mobileNavOpen) {
      // Prevent the body scrolling under the open mobile nav menu by fixing the body element,
      // setting the height to the viewport height and setting the overflow to hidden
      body.style.position = 'fixed'
      body.style.height = '100vh'
      body.style.width = '100vw'
      body.style.overflow = 'hidden'
      return
    }

    // Reset the body css to scroll and overflow normally.
    body.style.position = 'initial'
    body.style.height = 'initial'
    body.style.width = 'initial'
    body.style.overflow = 'initial'
  }, [isMobile, mobileNavOpen])

  const handleCloseClick = (event, track) => {
    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !isSsr && window.scrollTo({ top: 0, behavior: 'instant' })
    setMobileNavOpen(false)
  }

  const handleOpenClick = (event, track) => {
    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })
    setMobileNavOpen(true)
  }

  useEffect(() => {
    setLayoutMenuCondensed(isMobile ? mobileCondensed : condensed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, condensed, mobileCondensed])

  const callToAssociatedNumber = () => {
    if (phoneData?.phoneNumber) window.open(`tel:${phoneData.phoneNumber}`, '_self')
  }

  const handleCall = () => {
    if (isMobile) callToAssociatedNumber()
    else setButtonCall(!buttonCall)
  }

  const getSubNav = () => {
    if (!isMobile || !phoneData?.phoneNumber) return ''
    return (
      <Box className={'bg-neutral-50 flex flex-col gap-4 items-center p-6 lg:hidden'}>
        <Typography variant="bodyLg" fontWeight="regular" data-testid="header-subnav-phone-copy">
          {phoneData?.phoneCopy}
        </Typography>
        <AchieveLink
          className={''}
          noLink
          track={{
            list_name: 'PHONE CALL CLICK HEADER',
            click_id: phoneData?.phoneNumber,
            click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
              phoneData?.phoneNumber
            }`,
            click_url: phoneData?.phoneNumber,
            click_type: 'Phone Click',
            event_action: 'button_click',
            nav_link_section: 'Header',
            track_event: 'global_header',
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={getMobileIcon()}
            onClick={callToAssociatedNumber}
            data-testid="header-subnav-phone-button"
          >
            {phoneData?.phoneNumber}
          </Button>
        </AchieveLink>
      </Box>
    )
  }

  const getMobileIcon = () => {
    if (siteEnv === FfnPage || (siteEnv === AchievePage && isDebtConsolidation)) {
      return (
        <MediaImageStandard
          className={classNames(
            'object-none',
            'group-data-[product="website-ffn"]:object-contain',
            'group-data-[product="website-ffn"]:group-data-[slug="debt-consolidation"]:object-contain'
          )}
          alt={'phone icon'}
          height={20}
          content={{ url: phoneIconBlueUrl }}
          width={20}
          layout="fill"
          objectFit="contain"
          onClick={handleCall}
        />
      )
    }

    return (
      <Box position={'relative'}>
        <MediaImageStandard
          className={classNames(
            'object-none',
            'group-data-[product="website-ffn"]:object-contain',
            'group-data-[product="website-ffn"]:group-data-[slug="debt-consolidation"]:object-contain'
          )}
          alt={'phone icon'}
          height={condensed ? 20 : 24}
          content={{ url: phoneIconUrl }}
          width={condensed ? 20 : 24}
          layout="fill"
          objectFit="contain"
          onClick={handleCall}
        />
      </Box>
    )
  }

  const getCallDesktop = () => {
    return (
      <Box className="flex flex-row gap-2 cursor-pointer">
        <MediaImageStandard
          classNameContainer="relative"
          alt={'phone icon'}
          height={24}
          content={{ url: phoneIconBlueUrl }}
          width={24}
          layout="fill"
          onClick={handleCall}
        />
        <Typography variant="bodyLg" fontWeight="regular" component="p">
          {phoneData?.phoneNumber}
        </Typography>
      </Box>
    )
  }

  const getDebtConsultantCTA = () => {
    return (
      <div className="hidden justify-end lg:flex">
        <div className="flex items-center">
          <OwnerTypography
            className="text-[12px] leading-[140%]"
            fontWeight="regular"
            content="Talk to a certified debt consultant"
          />
          <AchieveLink
            noLink
            track={{
              list_name: 'CALL STICKY HEADER',
              click_id: phoneData?.phoneNumber,
              click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainName']} | ${
                phoneData?.phoneNumber
              }`,
              click_url: phoneData?.phoneNumber,
              click_type: 'Phone Click',
              event_action: 'button_click',
              nav_link_section: 'Above Header',
              track_event: 'phone_click',
            }}
            data-testid="debt-consultant-cta"
          >
            <Button
              className="hover:bg-[transparent] text-content-primary"
              variant="text"
              size="small"
              onClick={() => callToAssociatedNumber()}
            >
              <OwnerTypography
                variant="displayXxs"
                fontWeight="bold"
                content={phoneData?.phoneNumber}
              />
            </Button>
          </AchieveLink>
        </div>
      </div>
    )
  }

  const AchieveLogo = ({ mobile, logoLink }) => {
    /*Combination as follows:
      Mobile with Link
      Mobile without Link
      Desktop with Link
      Desktop without Link
    */
    return mobile ? (
      logoLink ? (
        <AchieveLink
          track={{
            list_name: 'Header Logo',
            click_id: `2022_${mainProductConfig[siteEnv || 'achieve']?.['MainName']?.replace(
              / /g,
              '_'
            )}_Logo_RGB`,
            click_text: `${
              mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
            } | Header logo`,
            nav_link_section: 'Header logo',
            track_event: 'global_header',
            event_action: 'link_click',
            event_type: 'link_click',
          }}
          href={backLink}
          withNextLink
          className={classNames(
            '[&_img]:w-[118px] [&_img]:h-[inherit] [&_img]:min-w-[inherit] [&_img]:max-w-[inherit]',
            'grow-0 shrink-0 h-[29px] absolute transition-[250ms_cubic-bezier(0.4,_0,_0.2,_1)] w-9 overflow-hidden lg:hidden',
            'group-data-[mobile-condensed=false]:group-data-[mobile-nav-open=false]:translate-y-[-90px]',
            'group-data-[product="website-tenant-2"]:h-[30px] group-data-[product="website-tenant-2"]:w-[70px]',
            'group-data-[product="website-ffn"]:h-[30px] group-data-[product="website-ffn"]:w-[70px]',
            'group-data-[product="website-fdr"]:h-auto group-data-[product="website-fdr"]:w-full group-data-[product="website-fdr"]:max-w-[70px] group-data-[product="website-fdr"]:max-h-[30px] group-data-[product="website-fdr"]:flex group-data-[product="website-fdr"]:items-center group-data-[product="website-fdr"]:justify-center',
            'group-data-[product="website-fdr"]:[&_img]:w-full group-data-[product="website-fdr"]:[&_img]:h-full group-data-[product="website-fdr"]:[&_img]:max-h-[30px] group-data-[product="website-fdr"]:[&_img]:object-contain',
            'group-data-[product="website-fdr"]:[&_svg]:w-full group-data-[product="website-fdr"]:[&_svg]:h-full group-data-[product="website-fdr"]:[&_svg]:max-h-[30px] group-data-[product="website-fdr"]:[&_svg]:object-contain',
            'group-data-[product="website-wfs"]:h-10 group-data-[product="website-wfs"]:w-[120px] group-data-[product="website-wfs"]:overflow-visible'
          )}
          data-testid={`${String(
            mainProductConfig[siteEnv || 'achieve']?.['MainName']
          )?.toLowerCase()}-header-mobile-logo`}
          onClick={() => setMobileNavOpen(false)}
        >
          <CurrentSvg siteEnv={siteEnv} className={'w-full h-auto min-w-[118px]'} />
        </AchieveLink>
      ) : (
        <div
          className={classNames(
            '[&_img]:w-[118px] [&_img]:h-[inherit] [&_img]:min-w-[inherit] [&_img]:max-w-[inherit]',
            'grow-0 shrink-0 h-[29px] absolute transition-[250ms_cubic-bezier(0.4,_0,_0.2,_1)] w-9 overflow-hidden lg:hidden',
            'group-data-[mobile-condensed=false]:group-data-[mobile-nav-open=false]:translate-y-[-90px]',
            'group-data-[product="website-tenant-2"]:h-[30px] group-data-[product="website-tenant-2"]:w-[70px]',
            'group-data-[product="website-ffn"]:h-[30px] group-data-[product="website-ffn"]:w-[70px]',
            'group-data-[product="website-fdr"]:h-auto group-data-[product="website-fdr"]:w-full group-data-[product="website-fdr"]:max-w-[70px] group-data-[product="website-fdr"]:max-h-[30px] group-data-[product="website-fdr"]:flex group-data-[product="website-fdr"]:items-center group-data-[product="website-fdr"]:justify-center',
            'group-data-[product="website-fdr"]:[&_img]:w-full group-data-[product="website-fdr"]:[&_img]:h-full group-data-[product="website-fdr"]:[&_img]:max-h-[30px] group-data-[product="website-fdr"]:[&_img]:object-contain',
            'group-data-[product="website-fdr"]:[&_svg]:w-full group-data-[product="website-fdr"]:[&_svg]:h-full group-data-[product="website-fdr"]:[&_svg]:max-h-[30px] group-data-[product="website-fdr"]:[&_svg]:object-contain',
            'group-data-[product="website-wfs"]:h-10 group-data-[product="website-wfs"]:w-[120px] group-data-[product="website-wfs"]:overflow-visible'
          )}
        >
          <CurrentSvg siteEnv={siteEnv} />
        </div>
      )
    ) : logoLink ? (
      <AchieveLink
        track={{
          ...{
            list_name: 'Header Logo',
            click_id: `${mainProductConfig[siteEnv || 'achieve']?.['HeaderLogo']}`,
            click_text: `${
              mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
            } | Header logo`,
            nav_link_section: 'Header logo',
            track_event: 'global_header',
            event_action: 'link_click',
            event_type: 'link_click',
          },
          ...logoEvent,
        }}
        href={backLink}
        withNextLink
        className={classNames(
          'h-[29px] w-[119px] flex grow-0 shrink-0 relative transition-[250ms_cubic-bezier(0.4,_0,_0.2,_1)] lg:h-[60px] lg:w-[180px] lg:[transform:none] lg:[tranform:translateY(0)]',
          'group-data-[mobile-nav-open="true"]:translate-y-[-90px]',
          'group-data-[mobile-condensed="true"]:translate-y-[-90px]',
          'group-data-[product="website-ffn"]:h-10',
          'group-data-[product="achieve"]:group-data-[slug="debt-consolidation"]:h-10',
          'group-data-[product="website-wfs"]:h-[62px] group-data-[product="website-wfs"]:[&_img]:max-h-full',
          'group-data-[product="website-wfs"]:group-data-[mobile-condensed=false]:pt-0',
          'group-data-[product="website-wfs"]:group-data-[mobile-condensed=false]:h-[62px] group-data-[product="website-wfs"]:group-data-[mobile-condensed=false]:pt-2',
          'lg:group-data-[mobile-condensed=true]:[transform:none]',
          'lg:group-data-[mobile-nav-open=true]:[transform:none]',
          'lg:group-data-[condensed=true]:h-[52px] lg:group-data-[condensed=true]:w-[117px]',
          'lg:group-data-[product="website-tenant-2"]:h-10 lg:group-data-[product="website-tenant-2"]:w-[90px]',
          'lg:group-data-[product="website-fdr"]:h-10 lg:group-data-[product="website-fdr"]:w-[90px]',
          'lg:group-data-[product="website-ffn"]:h-10 lg:group-data-[product="website-ffn"]:w-[90px]',
          'lg:group-data-[product="website-wfs"]:h-[87px] lg:group-data-[product="website-wfs"]:w-auto',
          'lg:group-data-[product="website-wfs"]:[&_img]:h-[87px] lg:group-data-[product="website-wfs"]:[&_img]:w-auto',
          'lg:group-data-[product="website-wfs"]:group-data-[condensed=true]:[&_img]:h-[68px] lg:group-data-[product="website-wfs"]:group-data-[condensed=true]:[&_img]:pt-4'
        )}
        data-testid={`${String(
          mainProductConfig[siteEnv || 'achieve']?.['MainName']
        )?.toLowerCase()}-header-logo`}
      >
        <CurrentSvg siteEnv={siteEnv} />
      </AchieveLink>
    ) : (
      <div
        className={classNames(
          'h-[29px] w-[119px] flex grow-0 shrink-0 relative transition-[250ms_cubic-bezier(0.4,_0,_0.2,_1)] lg:h-[60px] lg:w-[180px] lg:[transform:none]',
          'group-data-[mobile-nav-open=true]:translate-y-[-90px]',
          'group-data-[mobile-condensed=true]:translate-y-[-90px]',
          'group-data-[product="website-ffn"]:h-10',
          'group-data-[product="achieve"]:group-data-[slug="debt-consolidation"]:h-10',
          'group-data-[product="website-wfs"]:h-[62px] group-data-[product="website-wfs"]:[&_img]:max-h-full',
          'group-data-[product="website-wfs"]:group-data-[mobile-condensed=false]:pt-0',
          'group-data-[product="website-wfs"]:group-data-[mobile-condensed=false]:h-[62px] group-data-[product="website-wfs"]:group-data-[mobile-condensed=false]:pt-2',
          'lg:group-data-[mobile-condensed=true]:[transform:none]',
          'lg:group-data-[mobile-nav-open=true]:[transform:none]',
          'lg:group-data-[condensed=true]:h-[52px] lg:group-data-[condensed=true]:w-[117px]',
          'lg:group-data-[product="website-tenant-2"]:h-10 lg:group-data-[product="website-tenant-2"]:w-[90px]',
          'lg:group-data-[product="website-fdr"]:h-10 lg:group-data-[product="website-fdr"]:w-[90px]',
          'lg:group-data-[product="website-ffn"]:h-10 lg:group-data-[product="website-ffn"]:w-[90px]',
          'lg:group-data-[product="website-wfs"]:h-[87px] lg:group-data-[product="website-wfs"]:w-auto',
          'lg:group-data-[product="website-wfs"]:[&_img]:h-[87px] lg:group-data-[product="website-wfs"]:[&_img]:w-auto',
          'lg:group-data-[product="website-wfs"]:group-data-[condensed=true]:[&_img]:h-[68px] lg:group-data-[product="website-wfs"]:group-data-[condensed=true]:[&_img]:pt-4'
        )}
      >
        <CurrentSvg siteEnv={siteEnv} />
      </div>
    )
  }

  const PartnerLogo = ({ src }) => {
    return (
      <div className={'w-[120px] h-full lg:w-[180px] lg:h-full'}>
        <Image
          src={src}
          alt={`${mainProductConfig[siteEnv || 'achieve']?.['MainName']} partner logo`}
          className={'h-full w-full'}
          height="60"
          width="180"
          priority={true}
        />
      </div>
    )
  }

  const AchieveLogoMobile = useMemo(
    () => <AchieveLogo mobile={true} logoLink={logoLink} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logoLink, backLink]
  )

  const AchieveLogoDesktop = useMemo(
    () => <AchieveLogo mobile={false} logoLink={logoLink} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logoLink, backLink]
  )

  const callUsButton = useMemo(() => {
    if (siteEnv === AchievePage) {
      return (
        <Button
          color="secondary"
          variant="contained"
          className="w-full flex flex-row gap-2"
          size="small"
          onClick={() => callToAssociatedNumber()}
        >
          <MediaImageStandard
            className="object-contain relative"
            alt={'phone icon'}
            height={20}
            content={{ url: phoneIconUrl }}
            width={20}
            layout="fill"
          />
          <OwnerTypography variant="displayXs" fontWeight="bold" content={phoneData?.phoneNumber} />
        </Button>
      )
    }
    return (
      <Button
        color="secondary"
        variant="contained"
        className="w-full flex flex-row gap-2"
        size="small"
        onClick={() => callToAssociatedNumber()}
      >
        <OwnerTypography variant="displayXs" fontWeight="bold" content="Call Us" />
      </Button>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteEnv, phoneData?.phoneNumber])

  if (siteEnv === FdrPage && condensed) {
    return <FDRHeader siteEnv={siteEnv} tp_text={tp_text} tp_image={tp_image} linkUrl={linkUrl} />
  }

  return (
    <>
      <header
        className={classNames(
          'bg-white shadow-[0_0.5px_3px_rgb(16_24_40_/_9%),0_1px_1px_rgb(16_24_40_/_3%)] fixed w-full z-[15] lg:shadow-none',
          'lg:data-[condensed=true]:shadow-[0_6px_40px_-4px_rgb(16_24_40_/_11%),0_9px_12px_-3px_rgb(16_24_40_/_5%)]',
          'lg:data-[product="website-tenant-2"]:data-[selected=true]:after:content-[inherit]',
          'lg:data-[product="website-fdr"]:data-[selected=true]:after:content-[inherit]',
          'lg:data-[product="website-ffn"]:data-[selected=true]:after:content-[inherit]',
          'lg:data-[product="website-tenant-2"]:data-[nav="item-title"]:text-[13px] lg:data-[product="website-tenant-2"]:data-[nav="item-title"]:font-thin lg:data-[product="website-tenant-2"]:data-[nav="item-title"]:text-center lg:data-[product="website-tenant-2"]:data-[nav="item-title"]:block',
          'lg:data-[product="website-fdr"]:data-[nav="item-title"]:text-[13px] lg:data-[product="website-fdr"]:data-[nav="item-title"]:font-thin lg:data-[product="website-fdr"]:data-[nav="item-title"]:text-center lg:data-[product="website-fdr"]:data-[nav="item-title"]:block',
          'lg:data-[product="website-ffn"]:data-[nav="item-title"]:text-[13px] lg:data-[product="website-ffn"]:data-[nav="item-title"]:font-thin lg:data-[product="website-ffn"]:data-[nav="item-title"]:text-center lg:data-[product="website-ffn"]:data-[nav="item-title"]:block',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav"]:[&_div]:text-[13px] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav"]:[&_div]:font-thin',
          'lg:data-[product="website-fdr"]:data-[nav="sub-nav"]:[&_div]:text-[13px] lg:data-[product="website-fdr"]:data-[nav="sub-nav"]:[&_div]:font-thin',
          'lg:data-[product="website-ffn"]:data-[nav="sub-nav"]:[&_div]:text-[13px] lg:data-[product="website-ffn"]:data-[nav="sub-nav"]:[&_div]:font-thin',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav"]:px-4 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav"]:py-[6.4px]',
          'lg:data-[product="website-fdr"]:data-[nav="sub-nav"]:px-4 lg:data-[product="website-fdr"]:data-[nav="sub-nav"]:py-[6.4px]',
          'lg:data-[product="website-ffn"]:data-[nav="sub-nav"]:px-4 lg:data-[product="website-ffn"]:data-[nav="sub-nav"]:py-[6.4px]',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:translate-y-[69px]',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:translate-y-[69px] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:[overflow:initial]',
          'lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:translate-y-[69px] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:[overflow:initial]',
          'lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:translate-y-[69px] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:[overflow:initial]',
          'lg:data-[product="website-tenant-2"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-tenant-2"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-y-[55px]',
          'lg:data-[product="website-fdr"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-fdr"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-y-[55px]',
          'lg:data-[product="website-ffn"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-ffn"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-y-[55px]',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:bg-white lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:shadow-[-2.5px_-2.5px_2px_0_rgba(0_0_0_/_10%),inset_0_1px_3px_0_transparent] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:content-[""] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:h-[0.9285rem] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:absolute lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:top-[-0.4rem] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:rotate-45 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:w-[0.9285rem] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:left-5 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:right-0',
          'lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:bg-white lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:shadow-[-2.5px_-2.5px_2px_0_rgba(0_0_0_/_10%),inset_0_1px_3px_0_transparent] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:content-[""] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:h-[0.9285rem] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:absolute lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:top-[-0.4rem] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:rotate-45 lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:w-[0.9285rem] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:left-5 lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:right-0',
          'lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:bg-white lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:shadow-[-2.5px_-2.5px_2px_0_rgba(0_0_0_/_10%),inset_0_1px_3px_0_transparent] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:content-[""] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:h-[0.9285rem] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:absolute lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:top-[-0.4rem] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:rotate-45 lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:w-[0.9285rem] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:left-5 lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:right-0',
          'lg:data-[product="website-tenant-2"]:data-[nav-container="container"]:[&_li:first-child]:mt-2',
          'lg:data-[product="website-fdr"]:data-[nav-container="container"]:[&_li:first-child]:mt-2',
          'lg:data-[product="website-ffn"]:data-[nav-container="container"]:[&_li:first-child]:mt-2',
          'lg:data-[product="website-tenant-2"]:data-[nav-container="container"]:[&_li:first-child]:mt-2',
          'lg:data-[product="website-fdr"]:data-[nav-container="container"]:[&_li:first-child]:mt-2',
          'lg:data-[product="website-ffn"]:data-[nav-container="container"]:[&_li:first-child]:mt-2',
          'lg:data-[product="website-tenant-2"]:data-[nav-container="container"]:[&_li:last-child]:mt-2',
          'lg:data-[product="website-fdr"]:data-[nav-container="container"]:[&_li:last-child]:mt-2',
          'lg:data-[product="website-ffn"]:data-[nav-container="container"]:[&_li:last-child]:mt-2',
          'group'
        )}
        data-condensed={condensed}
        data-mobile-condensed={mobileCondensed}
        data-mobile-nav-open={mobileNavOpen}
        data-narrow={true}
        data-testid="acx-website-header"
        data-product={siteEnv}
        data-slug={params.slug}
      >
        {/* {showPhoneNumber && (
          <Head>
            <link rel="preload" href={phoneIconUrl} as="image" type="image/svg+xml" />
          </Head>
        )} */}
        <Container
          maxWidth={false}
          disableGutters
          className={classNames(
            'header-content-container',
            'max-w-[1440px] lg:pl-8 lg:pr-8',
            'data-[narrow=true]:max-w-[1280px]',
            '[div[page-main-variation="website-wfs"]_&]:data-[narrow=true]:max-w-[764px]'
          )}
          data-narrow={true}
        >
          {showDebtConsultantCta && !condensed && getDebtConsultantCTA()}
          <div
            className={classNames(
              'flex flex-col overflow-x-hidden lg:items-center lg:justify-between lg:flex-row lg:h-24 lg:overflow-visible lg:transition-[height] lg:duration-[250ms_cubic-bezier(0.4,_0,_0.2,_1)]',
              'group-data-[mobile-nav-open=true]:transition-[height] group-data-[mobile-nav-open=true]:duration-[0s] group-data-[mobile-nav-open=true]:h-screen',
              'lg:group-data-[mobile-nav-open=true]:h-24',
              'lg:group-data-[condensed=true]:h-16',
              'lg:group-data-[mobile-nav-open=true]:group-data-[condensed=true]:h-16'
            )}
          >
            {/* Container for content shown in the mobile header bar */}
            <div
              className={classNames(
                'bg-white items-center flex flex-row h-[72px] justify-between pl-4 pr-2 w-full z-[2] transition-[height] duration-[0.1s] ease-linear delay-[0.1s] lg:relative lg:w-auto lg:p-0',
                'group-data-[mobile-nav-open=true]:h-[54px] group-data-[mobile-nav-open=true]:group-data-[mobile-condensed=true]:h-[54px]',
                'group-data-[mobile-nav-open=true]:border-b-neutral-75 group-data-[mobile-nav-open=true]:border-b group-data-[mobile-nav-open=true]:border-solid group-data-[mobile-nav-open=true]:border-t-0 group-data-[mobile-nav-open=true]:border-x-0',
                'lg:[background:none] lg:group-data-[mobile-nav-open=true]:h-auto lg:group-data-[mobile-nav-open=true]:relative lg:group-data-[mobile-nav-open=true]:border-0',
                'data-[call-cta-mobile=true]:absolute data-[call-cta-mobile=true]:right-10',
                'group-data-[mobile-nav-open=true]:data-[call-cta-mobile=true]:hidden',
                'lg:group-data-[open-button-call=true]:px-4 lg:group-data-[open-button-call=true]:py-2 lg:group-data-[open-button-call=true]:rounded-3xl',
                'lg:group-data-[condensed=false]:group-data-[open-button-call=false]:min-w-[42px] lg:group-data-[condensed=false]:group-data-[open-button-call=false]:min-h-[42px] lg:group-data-[condensed=false]:group-data-[open-button-call=false]:w-[42px] lg:group-data-[condensed=false]:group-data-[open-button-call=false]:h-[42px] lg:group-data-[condensed=false]:group-data-[open-button-call=false]:p-1',
                'lg:group-data-[condensed=false]:group-data-[open-button-call=true]:min-h-[42px] lg:group-data-[condensed=false]:group-data-[open-button-call=true]:h-[42px]'
              )}
            >
              {partnerLogo ? (
                <div className={'flex'}>
                  {AchieveLogoDesktop}
                  <div
                    className={classNames(
                      'flex items-center mx-4 my-0',
                      'group-data-[condensed=true]:mx-4 group-data-[condensed=true]:my-0',
                      'group-data-[condensed=false]:mx-4 group-data-[condensed=false]:my-0',
                      'group-data-[mobile-condensed=false]:mx-4 group-data-[mobile-condensed=false]:my-0',
                      'group-data-[mobile-condensed=true]:ml-[-60px] group-data-[mobile-condensed=true]:mr-0 group-data-[mobile-condensed=true]:my-0'
                    )}
                  >
                    <Plus height={30} width={30} />
                  </div>
                  <PartnerLogo src={partnerLogo?.file?.url} />
                </div>
              ) : (
                AchieveLogoDesktop
              )}
              <div className="contents lg:hidden">{AchieveLogoMobile}</div>
              {/* Mobile nav menu open and close buttons */}
              <div className={'items-center flex h-[34px] justify-end relative w-full lg:hidden'}>
                {!mobileCondensed && showStickyFooter && (
                  <HeaderCta
                    linkUrl={linkUrl}
                    linkText={linkText}
                    size="small"
                    className={classNames('mr-10', {
                      'mr-[90px]': isMobile && phoneData?.phoneNumber,
                    })}
                    data-testid="header-get-started-button"
                  />
                )}
                {isMobile && phoneData?.phoneNumber && showPhoneNumber && (
                  <Box
                    className={classNames(
                      'group flex items-center cursor-pointer text-blue-500 gap-2 overflow-hidden transition-all duration-300 delay-150 z-[2] p-2 rounded-3xl [justify-content:left] bg-neutral-50',
                      'group-data-[mobile-condensed=true]:h-[34px] group-data-[mobile-condensed=true]:p-1',
                      'group-data-[condensed=true]:h-[34px] group-data-[condensed=true]:p-1',
                      'group-data-[product="website-ffn"]:p-0 group-data-[product="website-ffn"]:rounded-none group-data-[product="website-ffn"]:[background:none]',
                      'group-data-[condensed=true]:group-data-[open-button-call=false]:w-[34px]'
                    )}
                    data-call-cta-mobile={true}
                    data-open-button-call={buttonCall}
                  >
                    <AchieveLink
                      className={'w-6 h-6 flex justify-center items-center'}
                      noLink
                      track={{
                        list_name: 'PHONE CALL CLICK HEADER',
                        click_id: phoneData?.phoneNumber,
                        click_text: `${
                          mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                        } | ${phoneData?.phoneNumber}`,
                        click_url: phoneData?.phoneNumber,
                        click_type: 'Phone Click',
                        event_action: 'button_click',
                        nav_link_section: 'Header',
                        track_event: 'global_header',
                      }}
                      data-testid="mobile-phone-number-link"
                    >
                      {getMobileIcon()}
                    </AchieveLink>
                  </Box>
                )}
                <>
                  <button
                    onClick={(e) => {
                      handleOpenClick(e, {
                        nav_link_section: 'Toggle menu',
                        click_type: 'Button Click',
                        click_id: 'open button',
                      })
                    }}
                    className={classNames(
                      'items-center text-neutral-900 flex absolute transition-[250ms_cubic-bezier(0.4,_0,_0.2,_1)] px-2 py-[11px] border-0 [background:none]',
                      'group-data-[mobile-nav-open=true]:-translate-y-20',
                      'lg:hidden',
                      !showNav && 'hidden'
                    )}
                    data-testid="mobile-nav-open-button"
                    aria-label="Main navigation menu button"
                  >
                    <MenuIcon />
                  </button>
                  <button
                    onClick={(e) => {
                      handleCloseClick(e, {
                        nav_link_section: 'Toggle menu',
                        click_type: 'Button Click',
                        click_id: 'close button',
                      })
                    }}
                    role="button"
                    className={classNames(
                      'items-center text-neutral-900 flex absolute transition-[250ms_cubic-bezier(0.4,_0,_0.2,_1)] px-2 py-[11px] border-0 [background:none]',
                      '[&_span]:no-underline [&_span]:mr-3',
                      'group-data-[mobile-nav-open=false]:translate-x-[200px]',
                      'lg:hidden',
                      !showNav && 'hidden'
                    )}
                    data-testid="mobile-nav-close-button"
                    aria-label="Main navigation menu close button"
                  >
                    <Typography variant="bodyXs" fontWeight="medium" component="span">
                      CLOSE
                    </Typography>
                    <Close title="Close button" />
                  </button>
                </>
              </div>
            </div>
            {/* Main nav and sub nav list components */}
            {((showNav && !showStickyFooter) || (showNav && showStickyFooter && !condensed)) && (
              <Navigation
                event={linkEvent}
                mobileNavOpen={mobileNavOpen}
                items={mainNavigation}
                disabledRoutes={disabledRoutes}
                disabledElements={disabledElements}
                mobileSubnav={getSubNav()}
              />
            )}
            {/* Right Panel */}
            <div
              className={classNames(
                'group',
                'flex items-center absolute transition-[250ms_cubic-bezier(0.4,_0,_0.2,_1)] z-[2] gap-4 overflow-hidden translate-y-0 right-[95px] top-2.5 lg:relative lg:min-w-[280px] lg:max-w-[280px] lg:overflow-hidden lg:justify-end lg:right-[initial] lg:top-[initial]',
                '[&_button]:transition-[250ms_cubic-bezier(0.4,_0,_0.2,_1)]',
                'data-[none-phone-number=true]:right-[50px]',
                'data-[none-phone-number=false]:justify-end',
                'group-data-[mobile-nav-open=true]:translate-y-[-70px] group-data-[mobile-nav-open=true]:transition-all group-data-[mobile-nav-open=true]:duration-[250ms_cubic-bezier(0.4,_0,_0.2,_1)]',
                'group-data-[mobile-condensed=false]:translate-y-[-70px] group-data-[mobile-condensed=false]:transition-all group-data-[mobile-condensed=false]:duration-[250ms_cubic-bezier(0.4,_0,_0.2,_1)]',
                'lg:group-data-[mobile-condensed=false]:flex lg:group-data-[mobile-condensed=false]:translate-y-0',
                {
                  'max-w-max lg:max-w-max': showStickyFooter || siteEnv === FdrPage,
                }
              )}
              data-open-button-call={buttonCall}
              data-none-phone-number={phoneData?.phoneNumber ? false : true}
            >
              {!isMobile && phoneData?.phoneNumber && showPhoneNumber && isDebtConsolidation && (
                <Box className={'lg:[&_p]:font-[bold] lg:[&_span]:text-blue-800'}>
                  <AchieveLink
                    noLink
                    track={{
                      list_name: 'PHONE CALL CLICK HEADER',
                      click_id: phoneData?.phoneNumber,
                      click_text: `${
                        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                      } | ${phoneData?.phoneNumber}`,
                      click_url: phoneData?.phoneNumber,
                      click_type: 'Phone Click',
                      event_action: 'button_click',
                      nav_link_section: 'Header',
                      track_event: 'global_header',
                    }}
                    data-testid="mobile-phone-number-link"
                  >
                    {getCallDesktop()}
                  </AchieveLink>
                </Box>
              )}
              {phoneData?.phoneNumber &&
                !isMobile &&
                showPhoneNumber &&
                !condensed &&
                showStickyFooter && (
                  <Box
                    className={classNames(
                      'flex items-center cursor-pointer text-blue-500 gap-2 overflow-hidden transition-all duration-300 delay-150 z-[2] p-2 rounded-3xl [justify-content:left] bg-neutral-50',
                      'group-data-[mobile-condensed=true]:h-[34px] group-data-[mobile-condensed=true]:p-1',
                      'group-data-[condensed=true]:h-[34px] group-data-[condensed=true]:p-1',
                      'group-data-[product="website-ffn"]:p-0 group-data-[product="website-ffn"]:rounded-none group-data-[product="website-ffn"]:[background:none]',
                      'group-data-[condensed=true]:group-data-[open-button-call=false]:w-[34px]'
                    )}
                  >
                    <Box
                      className={classNames(
                        'flex flex-row items-center transition-all duration-300',
                        'data-[open-button-call=false]:pl-[12%]'
                      )}
                    >
                      {getMobileIcon()}
                      <Box
                        className={classNames(
                          'w-[125px] flex justify-center opacity-0 delay-[0ms] transition-opacity text-nowrap',
                          '[&_span]:leading-[100%]',
                          'group-data-[open-button-call=true]:opacity-100 group-data-[open-button-call=true]:delay-[310ms]'
                        )}
                      >
                        <AchieveLink
                          noLink
                          track={{
                            list_name: 'PHONE CALL CLICK HEADER',
                            click_id: phoneData?.phoneNumber,
                            click_text: `${
                              mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                            } | ${phoneData?.phoneNumber}`,
                            click_url: phoneData?.phoneNumber,
                            click_type: 'Phone Click',
                            event_action: 'button_click',
                            nav_link_section: 'Header',
                            track_event: 'global_header',
                          }}
                          data-testid="desktop-phone-number-link"
                        >
                          <Typography
                            variant="bodySm"
                            fontWeight="regular"
                            onClick={() => callToAssociatedNumber()}
                            data-testid="desktop-phone-number"
                          >
                            {phoneData?.phoneNumber}
                          </Typography>
                        </AchieveLink>
                      </Box>
                      <CloseIcon
                        className={''}
                        width={18}
                        height={18}
                        color={AchieveTheme?.ascend?.colors?.neutral?.[150]}
                        onClick={handleCall}
                      />
                    </Box>
                  </Box>
                )}
              {condensed && showStickyFooter && phoneData?.phoneNumber && (
                <div className="flex flex-row gap-2 hidden lg:block">
                  <AchieveLink
                    noLink
                    track={{
                      list_name: 'CALL STICKY HEADER',
                      click_id: phoneData?.phoneNumber,
                      click_text: `Call us`,
                      click_url: phoneData?.phoneNumber,
                      click_type: 'Call us Click',
                      event_action: 'button_click',
                      nav_link_section: 'Sticky Header',
                      track_event: 'call-sticky- hidden lg:block',
                    }}
                    data-testid="call-sticky- hidden lg:block"
                  >
                    {callUsButton}
                  </AchieveLink>
                </div>
              )}
              {/* "Get Started" CTA button is always present in desktop and is hidden and shown on
                scroll in mobile. */}
              {headerCta && (
                <HeaderCta
                  linkUrl={linkUrl}
                  linkText={linkText}
                  size="small"
                  data-condensed={isMobile || condensed}
                  className={classNames(
                    'inline-block opacity-0 delay-[0ms] transition-opacity absolute w-0 hover:no-underline text-nowrap',
                    '[&_button]:transition-all [&_button]:duration-[250msd_cubic-bezier(0.4,_0,_0.2,_1)] [&_button]:z-[1]',
                    'group-data-[condensed=false]:[&_button]:px-4 group-data-[condensed=false]:[&_button]:py-2',
                    'hover:[&>button]:text-blue-500 hover:[&>button]:bg-blue-200',
                    'group-data-[open-button-call=false]:opacity-100 group-data-[open-button-call=false]:w-auto group-data-[open-button-call=false]:relative',
                    {
                      'hidden lg:block': showStickyFooter,
                    }
                  )}
                  data-testid="header-get-started-button"
                  condensed={condensed}
                  textClassName={classNames({
                    'text-[13px] leading-[150%] font-bold': siteEnv === FdrPage,
                  })}
                />
              )}
              {((showSignIn && !showStickyFooter) ||
                (showSignIn && showStickyFooter && !condensed)) && (
                <SignInLink
                  signInLinkUrl={signInLinkUrl}
                  signInLinkText={signInLinkText}
                  event={signInEvent}
                  className={'hidden lg:inline-block lg:min-w-[60px] hover:text-blue-500'}
                  data-testid="header-sign-in-link"
                  disabledRoutes={disabledRoutes}
                  underline="hover"
                  outlined={siteEnv === FdrPage}
                  textClassName={classNames({
                    'text-[13px] leading-[150%] font-bold whitespace-nowrap': siteEnv === FdrPage,
                  })}
                />
              )}
              {condensed && showStickyFooter && (
                <div className="flex flex-row gap-2 hidden lg:block">
                  <div className="flex flex-row gap-2 items-center justify-center">
                    <div className="w-[165px] h-[20px]">
                      <MediaImageStandard content={tp_image} />
                    </div>
                    <OwnerTypography variant="bodyXs" className="text-black" content={tp_text} />
                  </div>
                </div>
              )}
            </div>
            {/* Action bar that shows at the bottom of the mobile nav */}
            {isMobile && (
              <HeaderMobileActions
                disabledRoutes={disabledRoutes}
                headerCta={headerCta}
                signInLinkText={signInLinkText}
                signInLinkUrl={signInLinkUrl}
                getStartedLinkUrl={linkUrl}
                getStartedLinkText={linkText}
              />
            )}
          </div>
        </Container>
        {condensed && showProgressPage && (
          <div
            className={classNames(
              'w-full h-[3px] absolute',
              '[background:linear-gradient(to_bottom,rgb(0_0_0_/_20%),rgb(0_0_0_/_0%),rgb(0_0_0_/_0%));]',
              '-z-10'
            )}
          >
            <div
              className={'content-[""] absolute h-[3px] bg-blue-400'}
              style={{ width: `${progressPage}%` }}
            />
          </div>
        )}
      </header>
    </>
  )
}
